import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import KeyValueItem from "entities/KeyValueItem";
import WorkDetails from "entities/WorkDetails";
import i18n from "i18n";
import { apiCallBegan } from "store/api";
import { AppDispatch, AppState } from "store/configureStore";
import SliceCollectionBase from "store/types/SliceCollectionBase";
import { authHeader } from "store/users/auth";

interface WorkDetailsResponse {
    ContractId: number;
    CompanyName: string;
    WorkAddress: string,
    DepartmentName: string,
    DepartmentId: number,
    CostCenterCode: string,
    WorkLocation: string,
    RemoteLocation: string,
    Position: string,
    PositionId: number | null,
    ManagerId: number | null,
    ManagerName: string,
    ManagerPositionId: number | null,
    ManagerPosition: string,
    MentorId: number | null,
    Mentor: string,
    CoordinatorId: number | null,
    Coordinator: string,
    UndergroundWorker: string,
    UndergroundWorkerId: number | null,
    PersonCode: string,
    PositionCode: string,
    Level: string,
    Segment: string,
    SegmentId: number| null,
    VirtualTeams: KeyValueResponse[]
   }

   interface KeyValueResponse {
      Id: number,
      Name: string
   }

interface State extends SliceCollectionBase<WorkDetails> { }

const initialState: State = {
  list: [],
  loading: false,
  lastFetch: null,
  error: null
}


const slice = createSlice({
    name: "workDetails",
    initialState,
    reducers: {
    workDetailsRequested: (details) => {
      details.loading = true;
      details.error = null;
    },
    workDetailsReceived: (details, action: PayloadAction<WorkDetailsResponse[]>) => {
      details.list = schema(action.payload);
      details.loading = false;
      details.lastFetch = Date.now();
    },
    workDetailsRequestFailed: (details, action) => {
      details.loading = false;
      details.error = action.payload;
    },
    workDetailsUpdated: (details, 
          action: PayloadAction<WorkDetailsResponse>) => {
            const index = details.list.findIndex(d => d.contractId === action.payload.ContractId);
            if (index !== -1) {
                 details.list[index] = mapDetails(action.payload);
            }
        }
   }
});

const {
  workDetailsRequested,
  workDetailsReceived,
  workDetailsRequestFailed,
  workDetailsUpdated
} = slice.actions;

export const getUsersWorkDetails = () => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (getState().entities.workDetails.loading) {
    return;
  }

  const userId = getState().users.auth.data?.WorkerId ?? 0;

  const url = `api/workdetails/user/${userId}`;

  var result =  dispatch(
      apiCallBegan({
          url,
          method: "GET",
          headers: { ...authHeader(), "Accept-Language": i18n.language },
          onStart: workDetailsRequested.type,
          onSuccess: workDetailsReceived.type,
          onError: workDetailsRequestFailed.type
      })
  );
  return result
};

export const updateWorkDetails = (details: WorkDetails) =>  async (dispatch: AppDispatch, getState: () => AppState) => {
    await dispatch(apiCallBegan({
      url: `/api/workdetails/contract/${details.contractId}`,
      method: "put",
      data: details,
      headers: {
        ...authHeader(),
        Accept: "application/json",
        "Content-Type": "application/json",
        "Accept-Language": i18n.language
      },
      onSuccess: workDetailsUpdated.type,
  }));
}

const schema = (payload: WorkDetailsResponse[]): WorkDetails[] => {
  if (!payload || payload.length === 0) {
    return [];
  }

    return payload.map(
      (resp:WorkDetailsResponse): WorkDetails => mapDetails(resp));
};

const mapDetails = (resp:  WorkDetailsResponse): WorkDetails => {
return {
      contractId: resp.ContractId,
      companyName: resp.CompanyName,
      workAddress: resp.WorkAddress,
      workLocation: resp.WorkLocation,
      remoteLocation: resp.RemoteLocation,
      department: resp.DepartmentName,
      departmentId: resp.DepartmentId,
      position: resp.Position,
      positionId: resp.PositionId,
      costCenterCode: resp.CostCenterCode,
      manager: resp.ManagerName,
      managerId: resp.ManagerId,
      managerPosition: resp.ManagerPosition,
      managerPositionId: resp.ManagerPositionId,
      mentor: resp.Mentor,
      mentorId: resp.MentorId,
      coordinator: resp.Coordinator,
      coordinatorId: resp.CoordinatorId,
      personCode: resp.PersonCode,
      positionCode: resp.PositionCode,
      udergroundWorker: resp.UndergroundWorker,
      undergroundWorkerId: resp.UndergroundWorkerId,
      level: resp.Level,
      segment: resp.Segment,
      segmentId: resp.SegmentId,
      virtualTeams: resp.VirtualTeams.map((item:KeyValueResponse):KeyValueItem => {return {id: item.Id,  value: item.Name}})
    }
}

export default slice.reducer;
